import { useEffect } from "react";
import type { CSSProperties } from "react";
import { WORKSHOPS } from "@/content/workshops";
import type { WorkshopStatus } from "@/content/workshops";
import { useAuth } from "@/hooks/use-auth";
import { useNavigation } from "@/stores/useNavigation";
import {
  WORKSHOP_TO_BUBBLE,
  useBubbleWorkshopLink,
} from "@/stores/useBubbleWorkshopLink";
import {
  hydrateAllRegisters,
  useProgramRollup,
  type WorkshopRollup,
} from "./useProgramRollup";

/**
 * PROG-ROLLUP — the program-level rollup panel on the Mission Control
 * capability-ring view (MC_00). Reads {@link useProgramRollup}, which
 * aggregates every register + progress at the edges; this component only
 * renders. It kicks off {@link hydrateAllRegisters} on mount and shows a
 * loading state until `ready`, so it never flashes zeros-then-jumps
 * (plan2 Risk #6).
 *
 * Workshop rows are clickable — they switch the left column to that
 * workshop's MC preview panel (`MC_WSn`), matching the bottom navigator.
 */
export function ProgramRollupPanel() {
  const rollup = useProgramRollup();
  // CREW-ROSTER — the program-overview tile combines progress (rollup) +
  // entity counts (rollup) + crew size (the auth roster, seed or live).
  const crewSize = useAuth().users.length;
  const setActivePanel = useNavigation((s) => s.setActivePanel);
  const hoveredBubbleId = useBubbleWorkshopLink((s) => s.hoveredBubbleId);
  const setHoveredWorkshop = useBubbleWorkshopLink((s) => s.setHoveredWorkshop);

  useEffect(() => {
    void hydrateAllRegisters();
  }, []);

  return (
    <div className="wr-rollup flex min-h-0 flex-1 flex-col" style={{ gap: 14 }}>
      <div style={eyebrowStyle}>Program Rollup</div>

      {!rollup.ready ? (
        <div className="flex flex-1 items-center justify-center">
          <span style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)", letterSpacing: 1 }}>
            Loading program rollup…
          </span>
        </div>
      ) : (
        <>
          {/* Headline metrics — workshops complete · crew · captured items */}
          <div style={{ display: "flex", gap: 8 }}>
            <Metric
              label="Workshops done"
              value={`${rollup.workshopsComplete}/${rollup.workshopsTotal}`}
              accent="var(--accent-green)"
            />
            <Metric label="Crew" value={String(crewSize)} accent="var(--accent-gold)" />
            <Metric
              label="Captured items"
              value={String(rollup.totals.total)}
              accent="var(--accent-true-blue)"
            />
          </div>

          {/* Program totals by category */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <Chip label="Decisions" n={rollup.totals.decisions} />
            <Chip label="Actions" n={rollup.totals.actions} />
            <Chip label="Assumptions" n={rollup.totals.assumptions} />
            <Chip label="Transcripts" n={rollup.totals.transcripts} />
            <Chip label="Captures" n={rollup.totals.captures} />
            <Chip label="Uploads" n={rollup.totals.uploads} />
          </div>

          {/* What's next — open actions across the program (Client US1) */}
          <RollupSection
            label="Upcoming actions"
            empty="No open actions."
            rows={rollup.upcomingActions.map((a) => ({
              key: a.id,
              primary: a.title,
              secondary: [a.owner, formatDue(a.due)].filter(Boolean).join(" · "),
              ws: a.workshopId,
            }))}
          />

          {/* Decisions made — latest decisions across the program (Client US1) */}
          <RollupSection
            label="Recent decisions"
            empty="No decisions logged yet."
            rows={rollup.recentDecisions.map((d) => ({
              key: d.id,
              primary: d.title,
              secondary: decisionStatusLabel(d.status),
              ws: d.workshopId,
              dot: decisionStatusColor(d.status),
            }))}
          />

          {/* Per-workshop breakdown */}
          <div
            className="wr-rollup-scroll"
            style={{ display: "flex", flexDirection: "column", gap: 7, overflowY: "auto", minHeight: 0 }}
          >
            {rollup.byWorkshop.map((w) => (
              <WorkshopRow
                key={w.workshopId}
                w={w}
                highlighted={
                  hoveredBubbleId !== null &&
                  WORKSHOP_TO_BUBBLE[w.workshopId] === hoveredBubbleId
                }
                onHoverChange={(hovering) =>
                  setHoveredWorkshop(hovering ? w.workshopId : null)
                }
                onSelect={() => {
                  const workshop = WORKSHOPS.find((x) => x.id === w.workshopId);
                  if (workshop) setActivePanel(workshop.panel);
                }}
              />
            ))}
          </div>
        </>
      )}

      <div style={hintStyle}>Select a workshop below to preview</div>
    </div>
  );
}

function WorkshopRow({
  w,
  onSelect,
  highlighted = false,
  onHoverChange,
}: {
  w: WorkshopRollup;
  onSelect: () => void;
  /** Lit up when this row's linked capability bubble is hovered on the
   *  MC_00 ring — see `useBubbleWorkshopLink`. */
  highlighted?: boolean;
  /** Fired on hover enter/leave so the MC_00 ring can highlight this
   *  row's linked capability bubble. */
  onHoverChange?: (hovering: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Preview ${w.workshopId} — ${w.title}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "8px 10px",
        borderRadius: 7,
        background: highlighted
          ? "rgba(var(--accent-true-blue-rgb), 0.10)"
          : "var(--bg-card)",
        border: `1px solid rgba(var(--accent-true-blue-rgb), ${highlighted ? 0.45 : 0.10})`,
        cursor: "pointer",
        textAlign: "left",
        width: "100%",
        font: "inherit",
        color: "inherit",
        transition: "border-color 0.15s, background 0.15s",
      }}
      onMouseEnter={(e) => {
        onHoverChange?.(true);
        e.currentTarget.style.borderColor =
          "rgba(var(--accent-true-blue-rgb), 0.35)";
        e.currentTarget.style.background =
          "rgba(var(--accent-true-blue-rgb), 0.06)";
      }}
      onMouseLeave={(e) => {
        onHoverChange?.(false);
        e.currentTarget.style.borderColor =
          "rgba(var(--accent-true-blue-rgb), 0.10)";
        e.currentTarget.style.background = "var(--bg-card)";
      }}
    >
      <span
        aria-hidden
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          flexShrink: 0,
          background: statusColor(w.status),
        }}
      />
      <span
        style={{
          fontSize: "var(--fs-label)",
          color: "var(--text-primary)",
          letterSpacing: 0.5,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={`${w.workshopId} — ${w.title}`}
      >
        {w.workshopId} · {w.title}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-label)",
          color: w.total > 0 ? "var(--text-secondary)" : "var(--text-muted)",
          flexShrink: 0,
        }}
      >
        {w.total}
      </span>
    </button>
  );
}

function Metric({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        padding: "10px 12px",
        borderRadius: 8,
        background: "var(--bg-card)",
        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
      }}
    >
      <div style={{ fontSize: "var(--fs-heading)", fontWeight: 700, color: accent, lineHeight: 1.1 }}>
        {value}
      </div>
      <div
        style={{
          fontSize: "var(--fs-label)",
          color: "var(--text-muted)",
          letterSpacing: 1,
          textTransform: "uppercase",
          marginTop: 2,
          // `flex: 1` alone doesn't stop a long unbreakable word like
          // "CAPTURED" (at this letter-spacing) from forcing the box past
          // its fair share and overflowing the rollup card on phone —
          // `minWidth: 0` above lets the box shrink, this lets the word
          // itself wrap instead of pushing past the edge.
          overflowWrap: "break-word",
        }}
      >
        {label}
      </div>
    </div>
  );
}

function Chip({ label, n }: { label: string; n: number }) {
  const muted = n === 0;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 9px",
        borderRadius: 999,
        fontSize: "var(--fs-label)",
        letterSpacing: 0.5,
        color: muted ? "var(--text-muted)" : "var(--text-secondary)",
        background: muted
          ? "transparent"
          : "rgba(var(--accent-true-blue-rgb), 0.08)",
        border: `1px solid ${muted ? "hsl(var(--border))" : "rgba(var(--accent-true-blue-rgb), 0.20)"}`,
      }}
    >
      {label}
      <strong style={{ color: muted ? "var(--text-muted)" : "var(--text-primary)" }}>{n}</strong>
    </span>
  );
}

type RollupRow = {
  key: string;
  primary: string;
  secondary: string;
  ws: string;
  /** Optional leading status dot colour (decisions). */
  dot?: string;
};

/**
 * A compact program-rollup list — "Upcoming actions" + "Recent decisions".
 * Each row shows the item title, a workshop badge, and a muted detail line
 * (owner·due, or decision status). Read-only: the editable surface is the
 * workshop log; this is the program-level "what's next / what did we decide".
 */
function RollupSection({
  label,
  rows,
  empty,
}: {
  label: string;
  rows: RollupRow[];
  empty: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
      <div
        style={{
          fontSize: "var(--fs-label)",
          letterSpacing: 1,
          textTransform: "uppercase",
          color: "var(--text-muted)",
        }}
      >
        {label}
      </div>
      {rows.length === 0 ? (
        <span style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>{empty}</span>
      ) : (
        rows.map((r) => (
          <div
            key={r.key}
            style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}
            title={`${r.ws} · ${r.primary}${r.secondary ? ` — ${r.secondary}` : ""}`}
          >
            {r.dot && (
              <span
                aria-hidden
                style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: r.dot }}
              />
            )}
            <span
              style={{
                fontSize: "var(--fs-label)",
                color: "var(--text-primary)",
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {r.primary}
            </span>
            {r.secondary && (
              <span
                style={{
                  fontSize: "var(--fs-micro)",
                  color: "var(--text-muted)",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {r.secondary}
              </span>
            )}
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-micro)",
                color: "var(--accent-sky)",
                letterSpacing: 0.4,
                flexShrink: 0,
              }}
            >
              {r.ws}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

/** Format an action's due date for the rollup ("" when undated). */
function formatDue(due: string): string {
  const d = due.trim();
  return d ? `due ${d}` : "";
}

function decisionStatusLabel(status: string): string {
  if (status === "agreed") return "Agreed";
  if (status === "parked") return "Parked";
  return "Proposed";
}

function decisionStatusColor(status: string): string {
  if (status === "agreed") return "var(--accent-green)";
  if (status === "parked") return "var(--text-muted)";
  return "var(--accent-true-blue)";
}

function statusColor(status: WorkshopStatus): string {
  if (status === "done") return "var(--accent-green)";
  if (status === "active") return "var(--accent-true-blue)";
  return "var(--text-muted)";
}

const eyebrowStyle: CSSProperties = {
  fontSize: "var(--fs-label)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--accent-true-blue)",
  fontWeight: 700,
  flexShrink: 0,
};

const hintStyle: CSSProperties = {
  flexShrink: 0,
  textAlign: "center",
  fontSize: "var(--fs-label)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  paddingTop: 4,
};
